import { state } from '../state';
import { filters } from '../state';
import type { Item } from '../types';
import { dom } from '../dom';
import {
  CX, CY,
  PERSISTENT_CLOCK_ENABLED,
  LOCK_WORLD_ZOOM, MENU_SCALE_FINAL,
  FEEDBACK_BASE_SCALE,
  CAMERA_MAX_PAN_BASE, CAMERA_MAX_PAN_PADDING, CAMERA_MAX_PAN_CAP,
  INSTALLER_SCANNER_SIZE_RATIO, INSTALLER_SCANNER_OBSTACLE_PADDING,
  INSTALLER_SCANNER_CLOCK_CLEARANCE, INSTALLER_SCANNER_SCENE_EDGE_PAD,
  INSTALLER_SCANNER_TRANSITION_MS, INSTALLER_SCANNER_FADE_START,
  INSTALLER_CENTERING_TIMEOUT_MS, INSTALLER_SCAN_TEXT_MS,
  INSTALLER_CENTER_EPSILON,
  INSTALLER_BIRTH_OFFSET_PAD, INSTALLER_BIRTH_RELEASE_MS, INSTALLER_TRAVEL_MS,
  INSTALLER_CROWD_REPEL_RANGE, INSTALLER_CROWD_REPEL_MAX, INSTALLER_CROWD_CLEARANCE,
  INSTALLER_CROWD_SMOOTHING, INSTALLER_CROWD_SMOOTHING_RETURN,
  INSTALLER_SCALE_GROW_FRACTION, INSTALLER_SCALE_RIPPLE_AMP,
  INSTALLER_SCALE_RIPPLE_WAVES, INSTALLER_SCALE_RIPPLE_DAMP,
  INSTALLER_BIRTH_HAPTIC_STRENGTH, INSTALLER_BIRTH_HAPTIC_DELAY_MS,
  INSTALLER_INSTALL_MS,
  INSTALLER_TEST_MANIFEST_URL,
  INSTALLER_STATES,
  ORGANISM_PAIR_GAP,
} from '../config';
import { COLORS, type AppDataEntry } from '../appData';
import {
  lerp, clamp, smoothstep,
  easeOutCubic, easeInOutCubic,
  normalizeVec,
} from '../utils/math';
import { hexToRgba } from '../utils/color';
import { isReducedMotion, getReducedMotionDuration } from '../utils/reducedMotion';
import { loop, hasHaptics } from '../loopSdk';
import { setHighlight } from '../items/highlight';
import { scrambleToText } from '../render/matrixScramble';
import { clearLinesCanvas, clearLockLinesOverlay } from '../render/lineRenderer';
import {
  isInstallerFlowActive,
  setInstallerFlowState,
  getSlotLayout, ensureRingGuide,
  getItemRenderScale, getItemSettledScale,
  applyItemTransform, applyItemSlot, setItemPose, setItemInstallArcVisible,
  createItemFromData,
  LINE_CENTER_RADIUS,
} from '../layout/ringLayout';
import { launchApp, getIsLaunching } from '../selection/launch';

// ── Unlock Transition ───────────────────────────────────────────────────

// setState is still in index.html (Task 28 will extract it).
let setState: (newState: string) => void = () => {};
export function setSetStateRef(fn: (newState: string) => void): void {
  setState = fn;
}

export function startUnlockTransition(): void {
  if (state.appState !== 'lock') return;
  state.unlockStartTime = performance.now();
  state.unlockKeyTimes = [];
  state.parallaxTargetX = 0; state.parallaxTargetY = 0;
  state.parallaxX = 0; state.parallaxY = 0;
  setState('unlocking');
  dom.reticle.classList.add('visible');
}

export function resetToLock(): void {
  if (state.appState === 'lock' || state.appState === 'locking') return;
  if (PERSISTENT_CLOCK_ENABLED && state.appState === 'home') {
    startLockingTransition();
    return;
  }
  state.cameraNormX = 0; state.cameraNormY = 0;
  state.cameraTargetX = 0; state.cameraTargetY = 0;
  state.cameraX = 0; state.cameraY = 0;
  state.worldZoom = LOCK_WORLD_ZOOM;
  state.menuScale = MENU_SCALE_FINAL;
  state.inputActive = false;
  state.feedbackScaleCurrent = FEEDBACK_BASE_SCALE;
  state.feedbackScaleTarget = FEEDBACK_BASE_SCALE;
  state.feedbackVelocity = 0;
  state.unlockKeyTimes = [];
  if (state.highlighted) { setHighlight(state.highlighted, false); state.highlighted = null; }
  scrambleToText('•');
  state.refQuat = null;
  state.homeRefQuat = null;
  filters.inputX.reset(); filters.inputY.reset();
  filters.cameraX.reset(); filters.cameraY.reset();
  document.querySelectorAll('.ring-guide').forEach(g => g.classList.remove('visible'));
  clearLinesCanvas();
  clearLockLinesOverlay();
  setState('lock');
}

export function startLockingTransition(): void {
  if (!PERSISTENT_CLOCK_ENABLED || state.appState !== 'home') return;
  state.lockingStartTime = performance.now();
  state.lockingStartCameraX = state.cameraX;
  state.lockingStartCameraY = state.cameraY;
  setState('locking');
}

export function getCameraTargetForItem(item: Item): { x: number; y: number } {
  // Once the scanner is open, the visible scanner sits at the shifted
  // anchor (not the icon's slot) — pan to it so the open camera stays
  // dead-centre on screen regardless of which slot the installer occupies.
  const usingScannerAnchor = item.kind === 'installer'
    && (item.scannerActive || (item.scannerOpenProgress || 0) > 0);
  const target = usingScannerAnchor
    ? getInstallerScannerAnchor(item)
    : { x: item.baseX, y: item.baseY };
  return {
    x: (target.x - CX) * state.menuScale * state.worldZoom,
    y: (target.y - CY) * state.menuScale * state.worldZoom,
  };
}

export function getDynamicCameraPanLimit(): number {
  if (!state.items.length) return CAMERA_MAX_PAN_BASE;
  const scale = Math.max(state.menuScale, 1);
  const zoom = Math.max(state.worldZoom, 1);
  let maxPan = CAMERA_MAX_PAN_BASE;
  for (const item of state.items) {
    if (!item) continue;
    const ox = (item.baseX - CX) * scale * zoom;
    const oy = (item.baseY - CY) * scale * zoom;
    const radius = (item.size * 0.5 + 8) * scale * zoom;
    const needed = Math.max(Math.abs(ox), Math.abs(oy)) + radius;
    if (needed > maxPan) maxPan = needed;
  }
  return clamp(maxPan + CAMERA_MAX_PAN_PADDING, CAMERA_MAX_PAN_BASE, CAMERA_MAX_PAN_CAP);
}

// Shifted anchor for the open scanner: pushed outward along the installer's
// radial direction so the open scanner circle clears both the clock guard
// (LINE_CENTER_RADIUS) and the scene edge. Mirrors the hover-grow contract
// implemented by clampNodeToTube.
export function getInstallerScannerAnchor(item: Item): { x: number; y: number } {
  const dx = item.baseX - CX;
  const dy = item.baseY - CY;
  const dist = Math.hypot(dx, dy);
  if (dist < 1) return { x: item.baseX, y: item.baseY };

  const sceneHalf = Math.min(CX, CY);
  const minDist = LINE_CENTER_RADIUS + INSTALLER_SCANNER_CLOCK_CLEARANCE;
  const maxDist = Math.max(minDist, sceneHalf - INSTALLER_SCANNER_SCENE_EDGE_PAD);
  const idealDist = Math.min(maxDist, Math.max(dist, (minDist + maxDist) * 0.5));

  const nx = dx / dist;
  const ny = dy / dist;
  return { x: CX + nx * idealDist, y: CY + ny * idealDist };
}

export function getInstallerScannerTargetScale(item: Item): number {
  if (!item) return 1;
  const sceneWidth = dom.scene ? (dom.scene.clientWidth || 800) : 800;
  const sceneHeight = dom.scene ? (dom.scene.clientHeight || 800) : 800;
  const sceneSize = Math.min(sceneWidth, sceneHeight);
  const sceneScale = Math.max(0.001, state.menuScale * state.worldZoom);

  let targetDiameter = sceneSize * INSTALLER_SCANNER_SIZE_RATIO;

  // Cap diameter so the scanner fits in the arc between the clock guard and
  // the scene edge once the installer is at its shifted anchor. Scene-coord
  // distances (LINE_CENTER_RADIUS, baseX/baseY) → screen px via sceneScale.
  const anchor = getInstallerScannerAnchor(item);
  const anchorDist = Math.hypot(anchor.x - CX, anchor.y - CY);
  const sceneHalf  = Math.min(CX, CY);
  const maxRadiusInner = Math.max(0, anchorDist - LINE_CENTER_RADIUS - INSTALLER_SCANNER_CLOCK_CLEARANCE);
  const maxRadiusOuter = Math.max(0, sceneHalf - anchorDist - INSTALLER_SCANNER_SCENE_EDGE_PAD);
  const maxRadiusScene = Math.max(40, Math.min(maxRadiusInner, maxRadiusOuter));
  const maxDiameterPx  = 2 * maxRadiusScene * sceneScale;
  if (targetDiameter > maxDiameterPx) targetDiameter = maxDiameterPx;

  // Account for currentScale (highlight/organism) and the press/ui multipliers
  // that will be applied to the rendered scanner — without these, the cap is
  // computed against the bare item size and the actual visible scanner can
  // still overshoot the clock guard when the installer is highlighted.
  const currentScale = Math.max(0.001, item.currentScale || 1);
  const uiScale     = Number.isFinite(item.uiScaleMul)   ? Math.max(0.001, item.uiScaleMul)   : 1;
  const pressScale  = Number.isFinite(item.pressScaleMul) ? Math.max(0.001, item.pressScaleMul) : 1;
  const nonScannerRenderScale = currentScale * uiScale * pressScale;
  const baseDiameter = Math.max(1, item.size * nonScannerRenderScale * sceneScale);
  return Math.max(1, targetDiameter / baseDiameter);
}

export function getInstallerScannerObstacleRadius(item: Item | null): number {
  if (!item || !item.scannerActive) return 0;
  return item.size * 0.5 * getItemRenderScale(item) + INSTALLER_SCANNER_OBSTACLE_PADDING;
}

export function applyInstallerScannerVisual(item: Item): void {
  if (!item || !item.scannerLayerEl) return;
  const open = clamp(item.scannerOpenProgress || 0, 0, 1);
  const layerVisible = item.scannerActive || open > 0.001;
  item.scannerLayerEl.style.display = layerVisible ? 'block' : 'none';
  item.scannerLayerEl.style.opacity = open.toFixed(3);

  const videoOpacity = item.scannerVideoLive
    ? clamp(item.scannerVideoAlpha || 0, 0, 1)
    : 0;
  if (item.scannerVideoEl) item.scannerVideoEl.style.opacity = videoOpacity.toFixed(3);
  if (item.scannerFallbackEl) item.scannerFallbackEl.style.opacity = item.scannerVideoLive ? '0' : '1';

  // Fade out the .item-inner placeholder background as the scanner opens —
  // when the icon is scaled up to the scanner size the bare background-color
  // shows as a soft halo around the camera circle (low-res-looking edge
  // because it's a single pixel CSS colour stretched across hundreds of
  // pixels). The scanner layer is opaque black, so cross-fading the
  // placeholder to transparent removes the halo without affecting the morph.
  if (item.inner) {
    const innerAlpha = clamp(1 - open, 0, 1);
    item.inner.style.backgroundColor = innerAlpha >= 0.999
      ? item.color
      : (innerAlpha <= 0.001 ? 'transparent' : hexToRgba(item.color, innerAlpha));
  }
}

export function resetInstallerScannerVisual(item: Item): void {
  if (!item || !item.scannerLayerEl) return;
  item.scannerActive = false;
  item.scannerOpenProgress = 0;
  item.scannerScale = 1;
  item.scannerVideoAlpha = 0;
  item.scannerVideoLive = false;
  item.el.classList.remove('scanner-host');
  if (item.scannerVideoEl) item.scannerVideoEl.style.opacity = '0';
  if (item.scannerFallbackEl) item.scannerFallbackEl.style.opacity = '1';
  if (item.scannerLayerEl) { item.scannerLayerEl.style.opacity = '0'; item.scannerLayerEl.style.display = 'none'; }
  applyItemTransform(item);
}

function beginInstallerScannerMorph(item: Item, mode: 'open' | 'close'): void {
  if (!item || !item.scannerLayerEl) return;
  item.scannerActive = true;
  item.el.classList.add('scanner-host');
  item.scannerLayerEl.style.display = 'block';
  state.scannerMorphAnim = {
    item,
    mode,
    startedAt: performance.now(),
    duration: INSTALLER_SCANNER_TRANSITION_MS,
    fromProgress: clamp(item.scannerOpenProgress || 0, 0, 1),
    toProgress: mode === 'open' ? 1 : 0,
  };
}

export function updateInstallerScannerMorph(now: number): void {
  const anim = state.scannerMorphAnim;
  if (!anim || !anim.item) return;
  const item = anim.item;
  if (!item.scannerLayerEl) {
    state.scannerMorphAnim = null;
    return;
  }

  const elapsed = now - anim.startedAt;
  const raw = clamp(elapsed / Math.max(1, anim.duration), 0, 1);
  const eased = anim.mode === 'open' ? easeOutCubic(raw) : raw * raw * raw;
  item.scannerOpenProgress = lerp(anim.fromProgress, anim.toProgress, eased);
  item.scannerScale = lerp(1, getInstallerScannerTargetScale(item), item.scannerOpenProgress);
  if (anim.mode === 'close') {
    const fadeStart = INSTALLER_SCANNER_FADE_START;
    item.scannerVideoAlpha = raw <= fadeStart
      ? 1
      : clamp(1 - (raw - fadeStart) / (1 - fadeStart), 0, 1);
  } else {
    item.scannerVideoAlpha = 1;
  }

  // Slide the installer outward along its radial direction as the scanner
  // opens (and back as it closes) so the open circle clears the clock.
  const anchor = getInstallerScannerAnchor(item);
  const t = clamp(item.scannerOpenProgress || 0, 0, 1);
  const rx = lerp(item.baseX, anchor.x, t);
  const ry = lerp(item.baseY, anchor.y, t);
  item.currentX = rx;
  item.currentY = ry;
  item.flowX    = rx;
  item.flowY    = ry;
  item.targetX  = rx;
  item.targetY  = ry;
  applyInstallerScannerVisual(item);
  applyItemTransform(item);

  if (raw < 1) return;
  const mode = anim.mode;
  state.scannerMorphAnim = null;

  if (mode === 'close') {
    stopScannerMedia();
    resetInstallerScannerVisual(item);
    return;
  }

  item.scannerOpenProgress = 1;
  item.scannerScale = getInstallerScannerTargetScale(item);
  item.scannerVideoAlpha = 1;
  applyInstallerScannerVisual(item);
  applyItemTransform(item);
  startScannerMedia(item);
}

function showScannerView(): void {
  if (!state.installerItem) return;
  state.inputActive = true;
  state.installerItem.scannerVideoLive = false;
  state.installerItem.scannerVideoAlpha = 0;
  // Kick the morph first so getCameraTargetForItem (which the per-frame
  // camera loop calls) sees scannerOpenProgress > 0 and routes to the
  // shifted scanner anchor on the very next frame.
  beginInstallerScannerMorph(state.installerItem, 'open');
  const camTarget = getCameraTargetForItem(state.installerItem);
  state.cameraTargetX = camTarget.x;
  state.cameraTargetY = camTarget.y;
  applyInstallerScannerVisual(state.installerItem);
}

function hideScannerView(): void {
  if (!state.installerItem || !state.installerItem!.scannerLayerEl) {
    stopScannerMedia();
    return;
  }
  if (!state.installerItem!.scannerActive && (state.installerItem!.scannerOpenProgress || 0) <= 0.001) {
    stopScannerMedia();
    resetInstallerScannerVisual(state.installerItem);
    return;
  }
  beginInstallerScannerMorph(state.installerItem, 'close');
}

function stopScannerMedia(): void {
  state.scannerMediaRequestId++;
  if (state.scannerMediaStream) {
    state.scannerMediaStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
  }
  state.scannerMediaStream = null;
  for (const item of state.items) {
    if (!item.scannerVideoEl) continue;
    try { item.scannerVideoEl.pause(); } catch (err) {}
    item.scannerVideoEl.srcObject = null;
    item.scannerVideoLive = false;
    item.scannerVideoAlpha = 0;
    applyInstallerScannerVisual(item);
  }
}

async function startScannerMedia(item: Item | null = state.installerItem): Promise<void> {
  const reqId = ++state.scannerMediaRequestId;
  if (!item || !item.scannerVideoEl || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    if (item) {
      item.scannerVideoLive = false;
      applyInstallerScannerVisual(item);
    }
    return;
  }

  if (state.scannerMediaStream) {
    item.scannerVideoEl.srcObject = state.scannerMediaStream;
    await item.scannerVideoEl.play().catch(() => {});
    if (reqId !== state.scannerMediaRequestId) return;
    item.scannerVideoLive = true;
    item.scannerVideoAlpha = 1;
    applyInstallerScannerVisual(item);
    return;
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 960 },
        height: { ideal: 960 },
        frameRate: { ideal: 30 },
      },
    });
    if (reqId !== state.scannerMediaRequestId) {
      stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      return;
    }
    state.scannerMediaStream = stream;
    item.scannerVideoEl.srcObject = state.scannerMediaStream;
    await item.scannerVideoEl.play().catch(() => {});
    if (reqId !== state.scannerMediaRequestId) {
      stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      return;
    }
    item.scannerVideoLive = true;
    item.scannerVideoAlpha = 1;
    applyInstallerScannerVisual(item);
  } catch (err) {
    if (reqId !== state.scannerMediaRequestId) return;
    console.warn('[Loop OS] Scanner camera unavailable, using fallback:', err);
    item.scannerVideoLive = false;
    item.scannerVideoAlpha = 0;
    applyInstallerScannerVisual(item);
  }
}

export function abortInstallerFlow(options: { keepReticleText?: boolean } = {}): void {
  const keepReticleText = !!options.keepReticleText;
  state.installerFlow!.requestToken++;
  state.installerFlow!.pendingInstallPayload = null;
  state.installerFlow!.installItem = null;
  state.installerFlow!.installDoneAt = 0;
  state.installerFlow!.centeringStartedAt = 0;
  state.installerFlow!.scannerCloseStartedAt = 0;
  state.installerFlow!.scannerObstacleGap = ORGANISM_PAIR_GAP;
  state.installerFlow!.scannerMessageUntil = 0;
  state.installerFlow!.scannerMessageReset = false;
  state.installConveyor = null;
  state.scannerMorphAnim = null;
  stopScannerMedia();

  if (state.highlighted) {
    setHighlight(state.highlighted, false);
    state.highlighted = null;
  }

  for (const item of state.items) {
    if (item.scannerLayerEl) resetInstallerScannerVisual(item);
    if (item.installState === 'inserting') {
      if (item.el && item.el.parentNode) item.el.parentNode.removeChild(item.el);
    } else if (item.installState === 'installing') {
      setItemInstallArcVisible(item, false);
      item.installState = 'ready';
    }
  }
  state.items = state.items.filter(item => item.installState !== 'inserting');

  setInstallerFlowState(INSTALLER_STATES.IDLE);
  if (!keepReticleText) scrambleToText('•');
}

export function startInstallerFlow(item: Item, options: { instantOpen?: boolean } = {}): void {
  if (state.appState !== 'home' || getIsLaunching() || !item || item.kind !== 'installer') return;
  if (isInstallerFlowActive()) return;
  const instantOpen = !!options.instantOpen;
  if (item.scannerLayerEl) resetInstallerScannerVisual(item);
  state.installerItem = item;
  state.installerSlotIndex = item.slotIndex;
  state.installerFlow!.pendingInstallPayload = null;
  state.installerFlow!.scannerObstacleGap = ORGANISM_PAIR_GAP;
  state.installerFlow!.scannerMessageReset = false;
  state.installerFlow!.scannerMessageUntil = 0;
  state.installerFlow!.centeringStartedAt = performance.now();
  state.installerFlow!.requestToken++;
  if (state.highlighted) {
    setHighlight(state.highlighted, false);
    state.highlighted = null;
  }
  state.inputActive = true;
  if (instantOpen) {
    const now = performance.now();
    state.installerFlow!.centeringStartedAt = 0;
    state.installerFlow!.scannerMessageUntil = now + INSTALLER_SCAN_TEXT_MS;
    state.installerFlow!.scannerMessageReset = false;
    scrambleToText(' look for compatible qr code ');
    showScannerView();
    setInstallerFlowState(INSTALLER_STATES.SCANNER_OPENING);
    return;
  }
  scrambleToText('•');
  setInstallerFlowState(INSTALLER_STATES.CENTERING);
}

export function cancelInstallerFlow(): void {
  if (!isInstallerFlowActive()) return;
  if (
    state.installerFlow!.state === INSTALLER_STATES.SCANNER_OPENING
    || state.installerFlow!.state === INSTALLER_STATES.SCANNING
    || state.installerFlow!.state === INSTALLER_STATES.PROCESSING
  ) {
    state.installerFlow!.requestToken++;
    state.installerFlow!.pendingInstallPayload = null;
    state.installerFlow!.scannerCloseStartedAt = performance.now();
    state.installerFlow!.scannerMessageUntil = 0;
    state.installerFlow!.scannerMessageReset = true;
    scrambleToText('•');
    hideScannerView();
    setInstallerFlowState(INSTALLER_STATES.SCANNER_CLOSING);
    return;
  }
  abortInstallerFlow({ keepReticleText: false });
}

function isDuplicateProject(projectId: string): boolean {
  if (!projectId) return false;
  return state.installedProjectIds.has(projectId);
}

function buildInstalledAppPayload(manifest: Record<string, unknown>, manifestUrl: string): AppDataEntry | null {
  const appName = typeof manifest.activityName === 'string' ? manifest.activityName.trim() : '';
  const cover = typeof manifest.iconPath === 'string' ? manifest.iconPath.trim() : '';
  const projectId = typeof manifest.projectId === 'string' ? manifest.projectId.trim() : '';
  if (!appName || !cover || !projectId) return null;
  return {
    name: appName,
    cover,
    appId: manifestUrl,
    edgeColor: COLORS[((state.items).length + 1) % COLORS.length],
    kind: 'activity',
    projectId,
    sessionInstalled: true,
    installState: 'inserting',
  };
}

export async function triggerCompatibleInstall(): Promise<void> {
  if (state.installerFlow!.state !== INSTALLER_STATES.SCANNING) return;

  const requestToken = ++state.installerFlow!.requestToken;
  setInstallerFlowState(INSTALLER_STATES.PROCESSING);
  scrambleToText(' Processing ');

  try {
    const response = await fetch(INSTALLER_TEST_MANIFEST_URL, { method: 'GET' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const manifest = await response.json();
    if (requestToken !== state.installerFlow!.requestToken) return;
    const payload = buildInstalledAppPayload(manifest, INSTALLER_TEST_MANIFEST_URL);
    if (!payload) throw new Error('Manifest missing required fields');
    if (isDuplicateProject(payload.projectId ?? '')) {
      scrambleToText(' already installed ');
      state.installerFlow!.scannerMessageUntil = performance.now() + 1300;
      state.installerFlow!.scannerMessageReset = false;
      setInstallerFlowState(INSTALLER_STATES.SCANNING);
      return;
    }
    state.installerFlow!.pendingInstallPayload = payload;
    state.installerFlow!.scannerCloseStartedAt = performance.now();
    scrambleToText(' crafting ');
    hideScannerView();
    setInstallerFlowState(INSTALLER_STATES.SCANNER_CLOSING);
  } catch (err) {
    if (requestToken !== state.installerFlow!.requestToken) return;
    console.warn('[Loop OS] Installer processing failed:', err);
    scrambleToText(' scan failed ');
    state.installerFlow!.scannerMessageUntil = performance.now() + 1300;
    state.installerFlow!.scannerMessageReset = false;
    setInstallerFlowState(INSTALLER_STATES.SCANNING);
  }
}

function chooseInstallerBirthSide(tempItem: Item): { x: number; y: number } {
  if (!state.installerItem) return { x: 1, y: 0 };
  const radial = normalizeVec(state.installerItem!.baseX - CX, state.installerItem!.baseY - CY, 0, -1);
  const right = { x: radial.y, y: -radial.x };
  const left = { x: -right.x, y: -right.y };

  const scoreSide = (dir: { x: number; y: number }) => {
    let score = 0;
    for (const item of state.items) {
      if (item === state.installerItem || item === tempItem || item.installState === 'inserting') continue;
      const dx = item.baseX - state.installerItem!.baseX;
      const dy = item.baseY - state.installerItem!.baseY;
      const dist = Math.hypot(dx, dy);
      if (dist < 0.0001) continue;
      const alignment = (dx * dir.x + dy * dir.y) / dist;
      if (alignment <= 0) continue;
      score += alignment / Math.max(1, dist);
    }
    return score;
  };

  const rightScore = scoreSide(right);
  const leftScore = scoreSide(left);
  return rightScore <= leftScore + 0.0001 ? right : left;
}

function playInstallerBirthHaptic(): void {
  if (!hasHaptics) return;
  loop!.haptics!.pulse(INSTALLER_BIRTH_HAPTIC_STRENGTH);
  setTimeout(() => {
    if (!hasHaptics) return;
    loop!.haptics!.pulse(INSTALLER_BIRTH_HAPTIC_STRENGTH);
  }, INSTALLER_BIRTH_HAPTIC_DELAY_MS);
}

function prepareConveyorShift(payload: AppDataEntry): void {
  const tempItem = createItemFromData(payload, state.installerSlotIndex);
  tempItem.currentScale = 1;
  tempItem.installState = 'inserting';
  tempItem.sessionInstalled = true;
  tempItem.el.style.opacity = '1';
  tempItem.el.style.pointerEvents = 'none';
  applyItemSlot(tempItem, state.installerSlotIndex, true);
  (state.items).push(tempItem);

  const tailSlot = Math.max(...(state.items).filter(item => item !== tempItem).map(item => item.slotIndex)) + 1;
  const tailLayout = getSlotLayout(tailSlot);
  ensureRingGuide(tailLayout.ringIndex);

  const sideVec = chooseInstallerBirthSide(tempItem);
  const birthOffset = ((state.installerItem ? state.installerItem!.size : 84) + tempItem.size) * 0.5 + INSTALLER_BIRTH_OFFSET_PAD;
  const birthStart = {
    x: state.installerItem ? state.installerItem!.baseX : CX,
    y: state.installerItem ? state.installerItem!.baseY : CY,
  };
  const birthEnd = {
    x: birthStart.x + sideVec.x * birthOffset,
    y: birthStart.y + sideVec.y * birthOffset,
  };
  const tailPoint = { x: tailLayout.x, y: tailLayout.y };
  const crowdItems = state.items.filter(item => item !== tempItem && item.installState !== 'inserting');
  const motionDuration = getReducedMotionDuration(INSTALLER_TRAVEL_MS, 340);
  const releaseDuration = getReducedMotionDuration(INSTALLER_BIRTH_RELEASE_MS, 140);
  const motionEndAt = motionDuration;
  const duration = motionEndAt + releaseDuration;

  setItemPose(tempItem, birthStart.x, birthStart.y, 0.08);
  playInstallerBirthHaptic();

  state.installConveyor = {
    startedAt: performance.now(),
    duration,
    releaseDuration,
    tempItem,
    tailSlot,
    motion: {
      duration: motionDuration,
      endAt: motionEndAt,
      p0x: birthStart.x,
      p0y: birthStart.y,
      p1x: birthEnd.x,
      p1y: birthEnd.y,
      p2x: tailPoint.x,
      p2y: tailPoint.y,
    },
    crowd: {
      items: crowdItems,
      range: INSTALLER_CROWD_REPEL_RANGE,
      maxPush: INSTALLER_CROWD_REPEL_MAX,
      clearance: INSTALLER_CROWD_CLEARANCE,
    },
  };
}

function updateConveyorShift(now: number): boolean {
  if (!state.installConveyor) return true;

  const elapsed = now - state.installConveyor.startedAt;
  const motion = state.installConveyor.motion;
  const crowd = state.installConveyor.crowd;

  const temp = state.installConveyor.tempItem;
  if (!temp) return true;
  const rawT = clamp(elapsed / Math.max(1, motion.duration), 0, 1);
  const t = easeInOutCubic(rawT);
  const omt = 1 - t;
  const moverX = omt * omt * motion.p0x + 2 * omt * t * motion.p1x + t * t * motion.p2x;
  const moverY = omt * omt * motion.p0y + 2 * omt * t * motion.p1y + t * t * motion.p2y;
  const scaleT = clamp(rawT / INSTALLER_SCALE_GROW_FRACTION, 0, 1);
  const scaleBase = easeOutCubic(scaleT);
  const rippleAmp = isReducedMotion() ? INSTALLER_SCALE_RIPPLE_AMP * 0.15 : INSTALLER_SCALE_RIPPLE_AMP;
  const rippleWaves = isReducedMotion() ? 1.25 : INSTALLER_SCALE_RIPPLE_WAVES;
  const ripple = rippleAmp > 0
    ? Math.sin(scaleT * Math.PI * rippleWaves) * Math.exp(-scaleT * INSTALLER_SCALE_RIPPLE_DAMP)
    : 0;
  let moverScale = lerp(0.08, 1, scaleBase) + ripple * rippleAmp;
  if (scaleT >= 1) {
    moverScale = lerp(moverScale, 1, clamp((rawT - INSTALLER_SCALE_GROW_FRACTION) / (1 - INSTALLER_SCALE_GROW_FRACTION), 0, 1));
  }
  moverScale = clamp(moverScale, 0.08, 1.12);
  setItemPose(temp, moverX, moverY, moverScale);

  if (crowd && crowd.items) {
    const motionFinished = elapsed > motion.endAt;
    const crowdReleaseT = motionFinished
      ? clamp((elapsed - motion.endAt) / Math.max(1, state.installConveyor.releaseDuration || INSTALLER_BIRTH_RELEASE_MS), 0, 1)
      : 0;
    const crowdWeight = 1 - smoothstep(crowdReleaseT);
    const crowdSmoothing = lerp(
      INSTALLER_CROWD_SMOOTHING,
      INSTALLER_CROWD_SMOOTHING_RETURN,
      crowdReleaseT
    );
    for (const item of crowd.items) {
      const baseX = item.baseX;
      const baseY = item.baseY;
      const dx = baseX - moverX;
      const dy = baseY - moverY;
      const dist = Math.hypot(dx, dy);
      const away = normalizeVec(dx, dy, baseX - CX, baseY - CY);
      const rangeFalloff = clamp(1 - dist / crowd.range, 0, 1);
      const fieldPush = crowd.maxPush * easeOutCubic(rangeFalloff) * crowdWeight;
      const minDist = temp.size * 0.5 + item.size * 0.5 + crowd.clearance;
      const collisionPush = Math.max(0, minDist - dist) * crowdWeight;
      const push = Math.max(fieldPush, collisionPush);
      const desiredX = baseX + away.x * push;
      const desiredY = baseY + away.y * push;
      const x = lerp(item.currentX, desiredX, crowdSmoothing);
      const y = lerp(item.currentY, desiredY, crowdSmoothing);
      const crowdScale = Number.isFinite(item.currentScale) && item.currentScale > 0
        ? item.currentScale
        : getItemSettledScale(item);
      setItemPose(item, x, y, crowdScale);
    }
  }

  return elapsed >= state.installConveyor.duration;
}

function finalizeConveyorShift(): void {
  if (!state.installConveyor) return;

  const installedItem = state.installConveyor.tempItem;
  if (!installedItem) return;
  installedItem.currentScale = 1;
  applyItemSlot(installedItem, state.installConveyor.tailSlot, true);
  installedItem.installState = 'installing';
  installedItem.el.style.pointerEvents = 'none';
  setItemInstallArcVisible(installedItem, true);
  state.lockHourItems = null;

  state.installerFlow!.installItem = installedItem;
  state.installerFlow!.installDoneAt = performance.now() + INSTALLER_INSTALL_MS;
  state.installConveyor = null;
  setInstallerFlowState(INSTALLER_STATES.INSTALLING);
}

function finalizeInstalledApp(item: Item): void {
  if (!item) return;
  item.installState = 'ready';
  item.el.style.pointerEvents = '';
  const projectId = item.projectId || '';
  if (projectId) state.installedProjectIds.add(projectId);
  setItemInstallArcVisible(item, false);
}

export function updateInstallerFlow(now: number): void {
  if (!isInstallerFlowActive()) return;

  if (state.installerFlow!.state === INSTALLER_STATES.CENTERING) {
    state.inputActive = true;
    if (!state.installerItem) {
      abortInstallerFlow({ keepReticleText: false });
      return;
    }
    const camTarget = getCameraTargetForItem(state.installerItem);
    const centered = Math.hypot(state.cameraX - camTarget.x, state.cameraY - camTarget.y) <= INSTALLER_CENTER_EPSILON;
    const timedOut = now - state.installerFlow!.centeringStartedAt >= INSTALLER_CENTERING_TIMEOUT_MS;
    if (centered || timedOut) {
      state.installerFlow!.scannerMessageUntil = now + INSTALLER_SCAN_TEXT_MS;
      state.installerFlow!.scannerMessageReset = false;
      scrambleToText(' look for compatible qr code ');
      showScannerView();
      setInstallerFlowState(INSTALLER_STATES.SCANNER_OPENING);
    }
    return;
  }

  if (state.installerFlow!.state === INSTALLER_STATES.SCANNER_OPENING) {
    state.inputActive = true;
    if (now >= state.installerFlow!.scannerMessageUntil && !state.installerFlow!.scannerMessageReset) {
      scrambleToText('•');
      state.installerFlow!.scannerMessageReset = true;
    }
    const scannerOpened = state.installerItem
      && state.installerItem!.scannerActive
      && state.installerItem!.scannerOpenProgress >= 0.999
      && !state.scannerMorphAnim;
    if (scannerOpened) {
      setInstallerFlowState(INSTALLER_STATES.SCANNING);
    }
    return;
  }

  if (state.installerFlow!.state === INSTALLER_STATES.SCANNING) {
    state.inputActive = true;
    if (now >= state.installerFlow!.scannerMessageUntil && !state.installerFlow!.scannerMessageReset) {
      scrambleToText('•');
      state.installerFlow!.scannerMessageReset = true;
    }
    return;
  }

  if (state.installerFlow!.state === INSTALLER_STATES.PROCESSING) return;

  if (state.installerFlow!.state === INSTALLER_STATES.SCANNER_CLOSING) {
    const scannerClosed = !state.installerItem || !state.installerItem!.scannerActive;
    if (!scannerClosed) return;
    if (state.installerFlow!.pendingInstallPayload) {
      prepareConveyorShift(state.installerFlow!.pendingInstallPayload);
      state.installerFlow!.pendingInstallPayload = null;
      state.installerFlow!.scannerCloseStartedAt = 0;
      setInstallerFlowState(INSTALLER_STATES.INSERTING);
    } else {
      abortInstallerFlow({ keepReticleText: false });
    }
    return;
  }

  if (state.installerFlow!.state === INSTALLER_STATES.INSERTING) {
    const done = updateConveyorShift(now);
    if (done) finalizeConveyorShift();
    return;
  }

  if (state.installerFlow!.state === INSTALLER_STATES.INSTALLING) {
    if (now >= state.installerFlow!.installDoneAt) {
      if (state.installerFlow!.installItem) finalizeInstalledApp(state.installerFlow!.installItem);
      state.installerFlow!.installItem = null;
      state.installerFlow!.installDoneAt = 0;
      setInstallerFlowState(INSTALLER_STATES.IDLE);
      scrambleToText('•');
    }
  }
}

export function runItemSelectionAction(item: Item): void {
  if (!item || state.appState !== 'home') return;
  if (isInstallerFlowActive()) return;
  if (item.kind === 'installer') {
    startInstallerFlow(item, { instantOpen: true });
    return;
  }
  if (item.appId) {
    launchApp(item);
  }
}

